// utils/timeFormat.js
// Utilidades de hora 24h escribibles para el CRM.
//
// Contexto: el <input type="time"> nativo del navegador muestra el formato AM/PM
// o 24h segun la REGION del sistema operativo del agente (no se puede forzar 24h
// de forma confiable cross-browser). Estas funciones permiten reemplazarlo por un
// input de texto que SIEMPRE trabaja en 24h, sin depender del locale.

/**
 * Normaliza una hora escrita libremente a formato 24h canonico "HH:MM" (cero-padded).
 * Acepta: "13:00", "9:00", "9", "13", "930", "1715", "9.30", "9h30", "17:5", "0900".
 * Devuelve "" si no se puede interpretar como hora valida (hora 0-23, minuto 0-59).
 *
 * El valor canonico SIEMPRE tiene hora y minuto de 2 digitos, de modo que pasa el
 * guard /^\d{2}:\d{2}$/ que usa NotesSection para construir next_followup_date.
 */
export function normalizeTime24(raw) {
  if (raw == null) return ''
  const s = String(raw).trim()
  if (s === '') return ''

  let h, m

  // 1) "HH:MM" / "H:M" con separador ":", "." o "h"
  const sep = s.match(/^(\d{1,2})\s*[:.hH]\s*(\d{1,2})$/)
  if (sep) {
    h = parseInt(sep[1], 10)
    m = parseInt(sep[2], 10)
  } else if (/^\d+$/.test(s)) {
    // 2) Solo digitos sin separador
    //    "9" -> 09:00, "13" -> 13:00, "930" -> 09:30, "1715" -> 17:15, "0900" -> 09:00
    if (s.length <= 2) {
      h = parseInt(s, 10); m = 0
    } else if (s.length === 3) {
      h = parseInt(s.slice(0, 1), 10); m = parseInt(s.slice(1), 10)
    } else if (s.length === 4) {
      h = parseInt(s.slice(0, 2), 10); m = parseInt(s.slice(2), 10)
    } else {
      return ''
    }
  } else {
    return ''
  }

  if (!Number.isInteger(h) || !Number.isInteger(m)) return ''
  if (h < 0 || h > 23 || m < 0 || m > 59) return ''
  return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0')
}

/**
 * Filtra los caracteres que el agente escribe en el input de hora: solo digitos y un
 * unico ":", maximo "HH:MM" (5 chars). NO reformatea ni cero-padea (eso ocurre al
 * salir del input) para que el cursor no salte mientras se escribe.
 */
export function sanitizeTimeTyping(raw) {
  if (raw == null) return ''
  let s = String(raw).replace(/[^\d:]/g, '')
  // colapsar a un solo ":"
  const first = s.indexOf(':')
  if (first !== -1) {
    s = s.slice(0, first + 1) + s.slice(first + 1).replace(/:/g, '')
  }
  return s.slice(0, 5)
}

/**
 * Inserta automaticamente el ":" mientras el agente tipea SOLO digitos, para orden
 * visual ("1615" se ve "16:15" sin que el agente escriba el separador). Regla de oro:
 * insertar UNICAMENTE cuando la interpretacion es inequivoca y NUNCA reordenar lo ya
 * mostrado (un ":" que salta de posicion en pleno tipeo confunde mas de lo que ayuda).
 *
 *   4 digitos: "1615" -> "16:15" si HH<=23 y MM<=59 (completo, inequivoco)
 *   3 digitos: "930" -> "9:30"  (93 no es hora valida: solo puede ser H:MM)
 *              "161" -> "16:1"  (1:61 no existe: solo puede ser prefijo HH:M)
 *              "123" -> "123"   (ambiguo: 1:23 o 12:3_ -> no tocar; blur/4to digito resuelve)
 *   resto (con ":", 1-2 digitos, invalidos): se devuelve tal cual.
 */
export function autoInsertColon(s) {
  if (!/^\d{3,4}$/.test(s)) return s
  if (s.length === 4) {
    const h = parseInt(s.slice(0, 2), 10), m = parseInt(s.slice(2), 10)
    return (h <= 23 && m <= 59) ? s.slice(0, 2) + ':' + s.slice(2) : s
  }
  // 3 digitos: ¿H:MM completa valida? ¿prefijo HH:M valido (minuto-decena <=5)?
  const hmm = parseInt(s.slice(1), 10) <= 59                                  // h = s[0] <= 9 siempre
  const hhm = parseInt(s.slice(0, 2), 10) <= 23 && parseInt(s[2], 10) <= 5
  if (hmm && !hhm) return s[0] + ':' + s.slice(1)
  if (!hmm && hhm) return s.slice(0, 2) + ':' + s[2]
  return s
}
